import { forwardRef, useCallback, useMemo } from 'react';

import { DragEndEvent, DragMoveEvent } from '@dnd-kit/core';
import { styled } from '@spazious/dollhouse-theme';

import { Flex, FlexProps } from '../Flex/index.js';

import { useDataTableContext } from './hooks/index.js';
import { getColumnGroups, getColumnId } from './utils/index.js';
import {
  DataTableColumnDraggable,
  DataTableColumnDraggableProps,
} from './DataTableColumnDraggable.js';
import { DataTableColumnGroup } from './DataTableColumnGroup.js';
import {
  DataTableColumnsDraggable,
  DataTableColumnsDraggableProps,
} from './DataTableColumnsDraggable.js';
import { DataTableHeaderCell, DataTableHeaderCellProps } from './DataTableHeaderCell.js';
import { DataTableColumnDefinition, DataTableRow } from './DataTableTypes.js';

export type DataTableHeaderProps<
  TRow extends DataTableRow = DataTableRow,
  TContext = unknown
> = Omit<FlexProps, 'children'> & {
  dense?: boolean;
  onColumnClick?: (
    evt: React.MouseEvent<HTMLDivElement>,
    column: DataTableColumnDefinition<TRow, TContext>
  ) => React.ReactNode;
  onColumnSort?: (event: DragMoveEvent | DragEndEvent, columnOrder: string[]) => void;
  cellComponent?: React.FC<DataTableHeaderCellProps<TRow, TContext>>;
};

export const DataTableHeader = styled(
  forwardRef(
    <TRow extends DataTableRow, TContext = unknown>(
      {
        onColumnSort,
        onColumnClick,
        cellComponent: HeaderCellComponent = DataTableHeaderCell as React.FC<
          DataTableHeaderCellProps<TRow, TContext>
        >,
        dense,
        ...restProps
      }: DataTableHeaderProps<TRow, TContext>,
      ref: React.ForwardedRef<HTMLDivElement>
    ) => {
      const { columns, update, context } = useDataTableContext<TRow, TContext>();

      const handleColumnSort = useCallback<
        PickProp<DataTableColumnsDraggableProps<TRow, TContext>, 'onColumnSort'>
      >(
        (evt, columnOrder) => {
          if (onColumnSort) onColumnSort(evt, columnOrder);
          update();
        },
        [onColumnSort, update]
      );

      const handleCellClick = useCallback<
        PickProp<DataTableHeaderCellProps<TRow, TContext>, 'onCellClick'>
      >(
        (evt, { column }) => {
          if (onColumnClick) {
            onColumnClick(evt, column);
          }
        },
        [onColumnClick]
      );

      const [pinnedLeftCols, flexCols, pinnedRightCols] = useMemo(() => {
        let colIdx = 0;
        return getColumnGroups(columns).map((columns) =>
          columns.map((column) => {
            const columnIndex = colIdx++;
            const { field, headerName = typeof field === 'string' ? field : '' } = column;

            const children = column.renderHeader ? (
              column.renderHeader({
                column,
                context,
              })
            ) : (
              <>{headerName}</>
            );

            const renderProp: PickProp<DataTableColumnDraggableProps<TRow>, 'children'> = ({
              dragHandler,
            }) => (
              <HeaderCellComponent
                children={children}
                column={column}
                columnIndex={columnIndex}
                context={context}
                dense={dense}
                dragHandler={dragHandler}
                rowIndex={0}
                tabIndex={0}
                onCellClick={handleCellClick}
              />
            );
            return (
              <DataTableColumnDraggable
                key={`data-table-header-column-${getColumnId(column)}`}
                column={column as any}
              >
                {renderProp}
              </DataTableColumnDraggable>
            );
          })
        );
      }, [columns, HeaderCellComponent, dense, handleCellClick, context]);

      return (
        <Flex ref={ref} {...restProps}>
          {!!pinnedLeftCols.length && (
            <DataTableColumnGroup pinned="left">
              <DataTableColumnsDraggable columns={columns} onColumnSort={onColumnSort}>
                {pinnedLeftCols}
              </DataTableColumnsDraggable>
            </DataTableColumnGroup>
          )}
          <DataTableColumnGroup>
            <DataTableColumnsDraggable columns={columns} onColumnSort={handleColumnSort}>
              {flexCols}
            </DataTableColumnsDraggable>
          </DataTableColumnGroup>
          {!!pinnedRightCols.length && (
            <DataTableColumnGroup pinned="right">
              <DataTableColumnsDraggable columns={columns} onColumnSort={onColumnSort}>
                {pinnedRightCols}
              </DataTableColumnsDraggable>
            </DataTableColumnGroup>
          )}
        </Flex>
      );
    }
  )
).withConfig({
  shouldForwardProp: (prop) => !['dense'].includes(prop),
  displayName: 'DataTableHeader',
})`
  display: flex;
  flex-direction: row;
  position: sticky;
  top: 0;
  z-index: 2;
  & > div:first-of-type > div:first-of-type > div {
    padding-left: ${({ dense }) => (dense ? '8px' : '16px')};
  }
  & > div:last-of-type > div:last-of-type > div {
    padding-right: ${({ dense }) => (dense ? '8px' : '16px')};
  }
`;
